import React, { type FC, type PropsWithChildren, useEffect } from 'react'
import { Box, Stack, type SxProps, type Theme, Typography } from '@mui/material'

import type { ReadonlyRecord } from '../../../../utils'

import { RuleEngineType, Translation } from '../../../../domain'

import { DetailsHeader, NoReRenderOnHover } from '../../common'
import { FormattedTranslation } from '../../Internationalization'
import type { JsonEditorFC } from '../../JsonEditor'
import { useAppUsecase } from '../../State'
import { EndpointsDropdown } from './EndpointDropdown'
import { useSelectedType } from './Hooks'
import { JsonInput } from './JsonInput'
import { LastMessage, LastMessageTitle } from './LastMessage'
import { TopicsInput } from './TopicInput'
import { TransformationInput, TransformationInputHelperText, TransformationInputTitle } from './TransformationInput'
import { TransformationOutput } from './TransformationOutput'
import { TypeSelector } from './TypeSelector'

const Title: FC<PropsWithChildren> = ({ children }) => (
    <Typography marginTop={2} marginBottom={1} variant="body2" fontWeight="bold">
        {children}
    </Typography>
)

const rootStyle: SxProps<Theme> = { display: 'flex', flexDirection: 'row', gap: 2 }
const additionalInputWrapperStyle: SxProps<Theme> = { flex: 1 }

const createSelectedTypeComponent = <Option, Props>(
    options: ReadonlyRecord<RuleEngineType, Option>,
    Component: FC<Readonly<Option & Omit<Props, keyof Option>>>
): FC<Omit<Props, keyof Option>> => {
    return (otherProps) => {
        const type = useSelectedType()
        return <Component {...otherProps} {...options[type]} />
    }
}

const SanboxAdditionalInput = createSelectedTypeComponent(
    {
        [RuleEngineType.ENDPOINT]: { title: Translation.SELECT_ENDPOINT, Input: EndpointsDropdown },
        [RuleEngineType.MQTT_TOPIC]: { title: Translation.INPUT_MQTT_TOPIC, Input: TopicsInput },
        [RuleEngineType.JSON]: { title: null, Input: null },
    },
    ({ title, Input }) =>
        title && Input
            ? (
                <>
                    <Title>
                        <FormattedTranslation id={title} />
                    </Title>
                    <Input />
                </>
            )
            : null
)

const SanboxPayloadInput = createSelectedTypeComponent<
    { TitleContent: FC, renderOnHover: boolean, Content: FC<{ Editor: JsonEditorFC }> },
    { Editor: JsonEditorFC }
>(
    {
        [RuleEngineType.ENDPOINT]: { TitleContent: LastMessageTitle, renderOnHover: true, Content: LastMessage },
        [RuleEngineType.MQTT_TOPIC]: { TitleContent: LastMessageTitle, renderOnHover: true, Content: LastMessage },
        [RuleEngineType.JSON]: {
            TitleContent: () => <FormattedTranslation id={Translation.TRANSFORMATION_INPUT} values={{ last: false }} />,
            renderOnHover: false,
            Content: JsonInput,
        },
    },
    ({ TitleContent, renderOnHover, Content, Editor }) => (
        <>
            {renderOnHover
                ? (
                    <NoReRenderOnHover>
                        <Title>
                            <TitleContent />
                        </Title>
                    </NoReRenderOnHover>
                )
                : (
                    <Title>
                        <TitleContent />
                    </Title>
                )}
            <Content Editor={Editor} />
        </>
    )
)

const SandboxTransformation = createSelectedTypeComponent(
    {
        [RuleEngineType.ENDPOINT]: { HelperText: TransformationInputHelperText },
        [RuleEngineType.MQTT_TOPIC]: { HelperText: TransformationInputHelperText },
        [RuleEngineType.JSON]: { HelperText: null },
    },
    ({ HelperText }) => (
        <>
            <Title>
                <TransformationInputTitle />
            </Title>
            <TransformationInput />
            {HelperText ? <HelperText /> : null}
        </>
    )
)

const SandboxOutput: FC = () => (
    <>
        <Title>
            <FormattedTranslation id={Translation.TRANSFORMATION_RESULT} />
        </Title>
        <TransformationOutput />
    </>
)

export const Sandbox: FC<{ Editor: JsonEditorFC }> = ({ Editor }) => {
    const start = useAppUsecase('startSandboxUsecase')
    useEffect(() => start.invoke(), [start])

    return (
        <>
            <DetailsHeader
                title={Translation.RULE_ENGINE_SANDBOX}
                description={[Translation.RULE_ENGINE_SANDBOX_DOCUMENTATION_TITLE, Translation.RULE_ENGINE_SANDBOX_DOCUMENTATION_BODY]}
            />
            <Box sx={rootStyle}>
                <Box>
                    <Title>
                        <FormattedTranslation id={Translation.SELECT_TRANSFORMATION_SOURCE} />
                    </Title>
                    <TypeSelector />
                </Box>
                <Box sx={additionalInputWrapperStyle}>
                    <SanboxAdditionalInput />
                </Box>
            </Box>
            <Stack>
                <SanboxPayloadInput Editor={Editor} />
                <SandboxTransformation />
                <SandboxOutput />
            </Stack>
        </>
    )
}
